
import {
  Link,
  useLocation,
  Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Receipt,
  FileBarChart,
  Megaphone,
  Send,
  CalendarDays,
  TrendingUp,
  MessageCircle,
  Shield,
  Menu,
  X,
  Zap,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Wallet,
  FileText,
  Repeat,
  RefreshCcw,
  Search,
  Plus,
  Smartphone,
  ClipboardCheck,
  Stethoscope,
  FileSignature,
  Database,
  IdCard,
  FileCheck2,
} from 'lucide-react';
import { useState, useMemo, useEffect, useRef } from 'react';
import './Layout.css';
import { modalidadesPorUsuario, TODAS_MODALIDADES } from '../services/modalidades';
import AvisosSino from './AvisosSino';

// Meu Perfil: item fixo no fim do menu, para TODO usuario logado (nao depende de permissao de modulo).
const IconeMeuPerfil = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="10" r="3" />
    <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662" />
  </svg>
);
const ITEM_MEU_PERFIL = { name: 'Meu Perfil', path: '/meu-perfil', icon: IconeMeuPerfil };

function ToothIcon({ size = 18, ...props }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M12 3.25c-2.3 0-3.2 1.2-4.8 1.2-2.2 0-3.7 1.8-3.7 4.2 0 2.6 1.4 4.6 2.5 6.8.8 1.7 1.1 5.3 2.8 5.3 1.1 0 1.3-1.7 1.7-3.3.3-1.4.7-2.1 1.5-2.1s1.2.7 1.5 2.1c.4 1.6.6 3.3 1.7 3.3 1.7 0 2-3.6 2.8-5.3 1.1-2.2 2.5-4.2 2.5-6.8 0-2.4-1.5-4.2-3.7-4.2-1.6 0-2.5-1.2-4.8-1.2Z" />
    </svg>
  );
}


const logoSoulidari = require('../assets/logo-soulidari.png');
const logoMini = require('../assets/logo-soulidari-mini.png');

// ═══════════════════════════════════════════════════════════════════════════════
// 🎯 SISTEMA CENTRALIZADO DE IDs - EVITA INCONSISTÊNCIAS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Normaliza IDs para comparar permissões (aceita tanto hífen quanto underscore)
 * Ex: 'gerar-cobranca' e 'gerar_cobranca' viram 'gerar-cobranca'
 */
const normalizeId = (id) => id.toLowerCase().replace(/_/g, '-');

/**
 * Verifica se o usuário tem permissão para um módulo
 * Compara IDs normalizados para evitar bugs de formatação
 */
const hasPermission = (userPermissions, moduleId) => {
  if (!userPermissions || userPermissions.length === 0) return true;
  const normalizedModuleId = normalizeId(moduleId);
  return userPermissions.some(perm => normalizeId(perm) === normalizedModuleId);
};

// Partes de módulo: quem tem só "clientes:consulta" vê "Meus Clientes" apenas com essa sub-opção
const PARTES_DO_MENU = {
  clientes:  { consulta_clientes: 'clientes:consulta', cadastro_clientes: 'clientes:cadastro' },
  checklist: { 'checklist-medico': 'checklist:medico', 'checklist-odontologia': 'checklist:odontologia', 'checklist-cartao': 'checklist:cartao' },
  agendamentos: {
    name: 'Agendamentos',
    icon: CalendarDays,
    children: [
      {
        id: 'agendamentos-medico',
        name: 'Médico',
        path: '/agendamentos/medico',
        icon: Stethoscope
      },
      {
        id: 'agendamentos-medico-conferencia',
        name: 'Conferência valores de exames',
        path: '/agendamentos/medico/conferencia-valores',
        icon: FileCheck2
      },
      {
        id: 'agendamentos-odontologia',
        name: 'Odontologia',
        path: '/agendamentos/odontologia',
        icon: ToothIcon
      },
      {
        id: 'agendamentos-carteira-odonto',
        name: 'Carteira Odonto',
        path: '/agendamentos/carteira-odonto',
        icon: IdCard
      },
      {
        id: 'agendamentos-medico-conferencia',
        name: 'Conferência valores de exames',
        path: '/agendamentos/medico/conferencia-valores',
        icon: FileCheck2
      }
    ]
  },

  'data-base': {
    name: 'Data Base',
    path: '/data-base',
    icon: Database
  },

  clientes: {
    name: 'Clientes',
    icon: Users,
    children: [
      { name: 'Meus Clientes', path: '/meus-clientes', icon: Users },
      { id: 'consulta_clientes', name: 'Consulta de Clientes', path: '/meus-clientes/consulta', icon: Search },
      { id: 'cadastro_clientes', name: 'Cadastro de Clientes', path: '/meus-clientes/cadastro', icon: Plus },
    ]
  },
  
  'repasse-parceiro': {
    name: 'Repasse Parceiro',
    icon: Repeat,
    children: [
      { id: 'repasse-parceiro:split-payment', name: 'Split Payment', path: '/repasse-parceiro/split-payment', icon: Wallet },
    ],
  },

  suporte: {
    name: 'Suporte',
    icon: MessageCircle,
    children: [
      { id: 'suporte_asaas', name: 'ASAAS', path: '/suporte/asaas', icon: CreditCard },
      { id: 'suporte_cpfl', name: 'CPFL', path: '/suporte/cpfl', icon: Zap },
      { id: 'suporte_pix_tools', name: 'PIX TOOLS', path: '/suporte/pix-tools', icon: Smartphone },
    ],
  },

  esteira_vendas: { 
    name: 'Esteira de Vendas', 
    path: '/esteira-vendas', 
    icon: TrendingUp 
  },
  
  pagamentos: { 
    name: 'Pagamentos', 
    path: '/pagamentos', 
    icon: CreditCard 
  },
  
  cobrancas: {
    name: 'Cobranças',
    icon: Wallet,
    children: [
      { titulo: 'Asaas', grupo: 'asaas' },
      { id: 'cobrancas_todas', grupo: 'asaas', name: 'Asaas · Todas', path: '/cobrancas/todas', icon: FileText },
      { id: 'cobrancas_avulsas', grupo: 'asaas', name: 'Asaas · Avulsas', path: '/cobrancas/avulsas', icon: Receipt },
      { id: 'cobrancas_parcelamentos', grupo: 'asaas', name: 'Asaas · Parcelamentos', path: '/cobrancas/parcelamentos', icon: Repeat },
      { id: 'cobrancas_assinaturas', grupo: 'asaas', name: 'Asaas · Assinaturas', path: '/cobrancas/assinaturas', icon: RefreshCcw },
      { titulo: 'CPFL', grupo: 'cpfl' },
      { id: 'cobrancas_cpfl', grupo: 'cpfl', name: 'CPFL', path: '/cobrancas/cpfl', icon: Zap },
      { id: 'cobrancas_pagantes_mes', grupo: 'cpfl', name: 'CPFL · Pagantes do Mês', path: '/cobrancas/pagantes-mes', icon: Zap },
      { titulo: 'Pix Santander', grupo: 'pix' },
      { id: 'cobrancas_pix', grupo: 'pix', name: 'Pix', path: '/cobrancas/pix', icon: Wallet },
    ]
  },
  
  'gerar-cobranca': { 
    name: 'Gerar Cobrança', 
    path: '/gerar-cobranca', 
    icon: Receipt 
  },
  
  relatorios: { 
    name: 'Relatórios', 
    path: '/relatorios', 
    icon: FileBarChart 
  },
  
  marketing: { 
    name: 'Marketing', 
    path: '/marketing', 
    icon: Megaphone 
  },
  
  relatorios_marketing: { 
    name: 'Relatórios Marketing', 
    path: '/relatorios-marketing', 
    icon: FileBarChart 
  },
  
  marketing_contacts: { 
    name: 'Contatos WhatsApp', 
    path: '/marketing-contacts', 
    icon: Users 
  },
  
  envio: { 
    name: 'Envio', 
    path: '/envio', 
    icon: Send 
  },
  
  checklist: {
    name: 'Checklist',
    icon: ClipboardCheck,
    children: [
      { id: 'checklist-medico', name: 'Médico', path: '/checklist/medico', icon: Stethoscope },
      { id: 'checklist-odontologia', name: 'Odontologia', path: '/checklist/odontologia', icon: ToothIcon },
      { id: 'checklist-cartao', name: 'Cartão', path: '/checklist/cartao', icon: CreditCard },
    ]
  },

  agendamentos: {
    name: 'Agendamentos',
    icon: CalendarDays,
    children: [
      { id: 'agendamentos-medico', name: 'Médico', path: '/agendamentos/medico', icon: Stethoscope },
      { id: 'agendamentos-odontologia', name: 'Odontologia', path: '/agendamentos/odontologia', icon: ToothIcon },
      { id: 'agendamentos-carteira-odonto', name: 'Carteira Odonto', path: '/agendamentos/carteira-odonto', icon: IdCard },
      {
        id: 'agendamentos-medico-conferencia',
        name: 'Conferência valores de exames',
        path: '/agendamentos/medico/conferencia-valores',
        icon: FileCheck2
      },
    ]
  },
  
  atendimento: { 
    name: 'Atendimento', 
    path: '/atendimento', 
    icon: MessageCircle 
  },
  
  administracao: { 
    name: 'Administração', 
    path: '/administracao', 
    icon: Shield 
  },

  clicksign: {
    name: 'Envios ClickSign',
    icon: FileSignature,
    children: [
      {
        id: 'clicksign_contrato_adesao',
        name: 'CONTRATO ADESÃO',
        path: '/clicksign/contrato-adesao',
        icon: FileText
      },
      {
        id: 'clicksign_termo_cancelamento',
        name: 'TERMO DE CANCELAMENTO',
        path: '/clicksign/termo-cancelamento',
        icon: FileText
      },
      {
        id: 'clicksign_acompanhar',
        name: 'ACOMPANHAR ENVIOS',
        path: '/clicksign/acompanhar',
        icon: Search
      }
    ]
  },

};

const ALL_NAV_ITEMS = {
  dashboard: {
    name: 'Dashboard',
    path: '/',
    icon: LayoutDashboard
  },

  'consulta-uc': {
    name: 'Consulta de UC',
    path: '/consulta-uc',
    icon: Search
  },

  'data-base': PARTES_DO_MENU['data-base'],

  clientes: PARTES_DO_MENU.clientes,

  'repasse-parceiro': PARTES_DO_MENU['repasse-parceiro'],

  suporte: PARTES_DO_MENU.suporte,

  cobrancas: PARTES_DO_MENU.cobrancas,

  'gerar-cobranca': PARTES_DO_MENU['gerar-cobranca'],

  relatorios: PARTES_DO_MENU.relatorios,

  marketing: PARTES_DO_MENU.marketing,

  checklist: {
    name: 'Checklist',
    icon: ClipboardCheck,
    children: [
      { id: 'checklist-medico', name: 'Médico', path: '/checklist/medico', icon: Stethoscope },
      { id: 'checklist-odontologia', name: 'Odontologia', path: '/checklist/odontologia', icon: ToothIcon },
      { id: 'checklist-cartao', name: 'Cartão', path: '/checklist/cartao', icon: CreditCard },
    ]
  },

  agendamentos: {
    name: 'Agendamentos',
    icon: CalendarDays,
    children: [
      {
        id: 'agendamentos-medico',
        name: 'Médico',
        path: '/agendamentos/medico',
        icon: Stethoscope
      },
      {
        id: 'agendamentos-odontologia',
        name: 'Odontologia',
        path: '/agendamentos/odontologia',
        icon: ToothIcon
      },
      {
        id: 'agendamentos-carteira-odonto',
        name: 'Carteira Odonto',
        path: '/agendamentos/carteira-odonto',
        icon: IdCard
      },
      {
        id: 'agendamentos-medico-conferencia',
        name: 'Conferência valores de exames',
        path: '/agendamentos/medico/conferencia-valores',
        icon: FileCheck2
      }
    ]
  },

  atendimento: PARTES_DO_MENU.atendimento,

  administracao: PARTES_DO_MENU.administracao,

  clicksign: PARTES_DO_MENU.clicksign
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🔍 VALIDAÇÃO - Alerta no console sobre IDs inconsistentes
// ═══════════════════════════════════════════════════════════════════════════════

if (process.env.NODE_ENV === 'development') {
  const moduleIds = Object.keys(ALL_NAV_ITEMS);
  const inconsistent = moduleIds.filter(id => id.includes('_'));
  if (inconsistent.length > 0) {
    console.warn(
      '⚠️ [Layout] IDs com underscore detectados:',
      inconsistent,
      '\n💡 Dica: Use hífen (-) nos IDs do banco de dados para consistência.'
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════════════

// Permissões específicas de subopções do menu.
function temParte(userPermissions, moduleId) {
  if (!Array.isArray(userPermissions) || userPermissions.length === 0) {
    return false;
  }

  const modulo = normalizeId(moduleId);

  return userPermissions.some((permissao) => {
    const valor = normalizeId(permissao);
    return valor.startsWith(`${modulo}:`);
  });
}

// Itens que o módulo inteiro NÃO libera: exigem a permissão específica (ou admin).
const ITENS_INDEPENDENTES = {
  'agendamentos-medico-conferencia': 'agendamentos:conferencia-valores-exames',
};

function filtrarPartes(moduleId, item, userPermissions, isAdmin = false) {
  if (!item || !Array.isArray(item.children)) {
    return item;
  }

  // Permissão do módulo principal libera as opções dele, exceto as independentes.
  if (hasPermission(userPermissions, moduleId)) {
    if (isAdmin || !userPermissions || userPermissions.length === 0) return item;
    return {
      ...item,
      children: item.children.filter((child) => {
        const exigida = ITENS_INDEPENDENTES[normalizeId(child.id || '')];
        return !exigida || hasPermission(userPermissions, exigida);
      })
    };
  }

  const modulo = normalizeId(moduleId);

  const children = item.children.filter((child) => {
    // Cabeçalhos visuais, como Asaas e CPFL, não são opções clicáveis.
    if (!child.id || child.titulo || child.grupo) {
      return true;
    }

    const childId = normalizeId(child.id);

    if (hasPermission(userPermissions, childId)) {
      return true;
    }

    if (hasPermission(userPermissions, `${modulo}:${childId}`)) {
      return true;
    }

    // O id do item no menu não é igual ao nome da permissão parcial gravada.
    const PARTES_POR_ITEM = {
      'agendamentos-medico': 'agendamentos:medico',
      'agendamentos-odontologia': 'agendamentos:odontologia-agenda',
      'agendamentos-carteira-odonto': 'agendamentos:odontologia',
      'agendamentos-medico-conferencia': 'agendamentos:conferencia-valores-exames',
      'checklist-medico': 'checklist:medico',
      'checklist-odontologia': 'checklist:odontologia',
      'checklist-cartao': 'checklist:cartao',
      'clicksign-contrato-adesao': 'clicksign:contrato',
      'clicksign-termo-cancelamento': 'clicksign:termo',
      'clicksign-acompanhar': 'clicksign:acompanhar',
    };
    if (PARTES_POR_ITEM[childId]) {
      return hasPermission(userPermissions, PARTES_POR_ITEM[childId]);
    }

    return false;
  });

  return {
    ...item,
    children
  };
}

// 🎨 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState({});
  const [openBillingGroups, setOpenBillingGroups] = useState({});
  const { user } = useAuth();
  const location = useLocation();

  const userPermissions = user?.permissions || [];

  // ✅ FILTRO INTELIGENTE - Usa comparação normalizada
  const navItems = useMemo(() => {
    const isGestor = (user?.roles || []).some((role) =>
      String(role).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'gestor'
    );

    // Sem permissões individuais configuradas: mostra todos os módulos
    if (userPermissions.length === 0) {
      const adminOuGestor = isGestor || (user?.roles || []).some((role) =>
        String(role).trim().toLowerCase() === 'admin'
      );
      return Object.entries(ALL_NAV_ITEMS)
        .filter(([id]) => id !== 'suporte' || adminOuGestor)
        .map(([, item]) => item);
    }
    
    // Filtra usando comparação normalizada (aceita hífen E underscore)
    const filtered = Object.entries(ALL_NAV_ITEMS)
        .filter(([id]) => id === 'data-base'
          ? (
              hasPermission(userPermissions, 'data-base') ||
              (user?.roles || []).some((role) =>
                ['admin', 'gestor'].includes(
                  String(role).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
                )
              )
            )
          : (hasPermission(userPermissions, id) || temParte(userPermissions, id) || (id === 'repasse-parceiro' && isGestor)))
      .map(([id, item]) => filtrarPartes(id, item, userPermissions, (user?.roles || []).some((role) => String(role).trim().toLowerCase() === 'admin')));
    
    // 🔍 Log de debug no desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 [Layout] Usuário:', user?.name);
      console.log('📋 [Layout] Permissões:', userPermissions);
      console.log('✅ [Layout] Módulos visíveis:', filtered.map(i => i.name));
    }
    
    return filtered;
  }, [userPermissions, user]);

  const collapsedRef = useRef(false);
  const menuManualRef = useRef(false);
  const menuAutoRef = useRef(false);
  useEffect(() => { collapsedRef.current = collapsed; }, [collapsed]);

  const toggleCollapse = () => {
    menuManualRef.current = true;
    menuAutoRef.current = false;
    setCollapsed(!collapsed);
  };

  // Recolhe o menu sozinho em telas menores (1024px a 1599px) e reexpande ao voltar.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 1599px)');
    const aplicar = (pequena) => {
      if (pequena) {
        if (menuManualRef.current) return;
        if (!collapsedRef.current) { menuAutoRef.current = true; setCollapsed(true); }
      } else {
        if (menuAutoRef.current) { setCollapsed(false); menuAutoRef.current = false; }
        menuManualRef.current = false;
      }
    };
    aplicar(mq.matches);
    const aoMudar = (e) => aplicar(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', aoMudar);
    else mq.addListener(aoMudar);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', aoMudar);
      else mq.removeListener(aoMudar);
    };
  }, []);

  useEffect(() => {
    let anterior = null;
    const aoMudar = (e) => {
      if (e.detail && e.detail.ampliado) {
        setCollapsed((atual) => { if (anterior === null) anterior = atual; return true; });
      } else if (anterior !== null) {
        setCollapsed(anterior);
        anterior = null;
      }
    };
    window.addEventListener('souli:ampliar-tabela', aoMudar);
    return () => window.removeEventListener('souli:ampliar-tabela', aoMudar);
  }, []);
  
  const aoNavegarMenu = () => {
    setSidebarOpen(false);
    try {
      if (window.matchMedia && window.matchMedia('(min-width: 1024px) and (max-width: 1599px)').matches) {
        menuManualRef.current = false;
        menuAutoRef.current = true;
        setCollapsed(true);
      }
    } catch (_) { /* sem matchMedia */ }
  };

  const toggleSubMenu = (itemName) => {
    setOpenSubMenu(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const toggleBillingGroup = (groupKey) => {
    setOpenBillingGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const isChildActive = (children) => {
    return children?.some(child =>
      child.path && (
        location.pathname === child.path ||
        location.pathname.startsWith(child.path + '/')
      )
    );
  };

  // Ao navegar para uma página de dentro de um grupo, volta ao padrão (grupo aberto).
  // O fechamento manual continua valendo enquanto a pessoa não trocar de página.
  useEffect(() => {
    setOpenSubMenu(prev => {
      const proximo = { ...prev };
      let mudou = false;
      navItems.forEach(item => {
        if (item.children && isChildActive(item.children) && item.name in proximo) {
          delete proximo[item.name];
          mudou = true;
        }
      });
      return mudou ? proximo : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const ativo = navItems
      .flatMap(item => (item.children || []).map(child => ({ item, child })))
      .find(({ child }) =>
        child.grupo &&
        child.path &&
        (
          location.pathname === child.path ||
          location.pathname.startsWith(child.path + '/')
        )
      );

    if (!ativo) return;

    const groupKey = `${ativo.item.name}:${ativo.child.grupo}`;
    setOpenBillingGroups(prev =>
      prev[groupKey] ? prev : { ...prev, [groupKey]: true }
    );
  }, [location.pathname, navItems]);

  // Menu de Cobranças: esconde os grupos de modalidade que o perfil não pode usar
  // (mesma regra da tela Gerar Cobrança; sem nenhuma modalidade marcada = todas).
  const modalidadesMenu = modalidadesPorUsuario(user);
  const filhosVisiveis = (item) => (item.children || []).filter(
    (child) => !child.grupo || !TODAS_MODALIDADES.includes(child.grupo) || modalidadesMenu.includes(child.grupo)
  );

  // Quem não tem a modalidade Pix não abre a página pelo endereço direto.
  useEffect(() => {
    if (location.pathname.startsWith('/cobrancas/pix') && !modalidadesMenu.includes('pix')) {
      window.location.replace('/gerar-cobranca');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]);

  return (
    <div className="layout-container">
      {sidebarOpen && <div className="mobile-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-area">
            <img src={logoSoulidari} alt="Logo Soulidari" className="logo-image-sidebar" />
            <img src={logoMini} alt="Soulidari" className="logo-image-mini" />
          </div>
          <button className="collapse-toggle" onClick={toggleCollapse} title={collapsed ? 'Expandir menu' : 'Recolher menu'}>
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        
        <nav className="sidebar-nav">
            <AvisosSino collapsed={collapsed} />
          {[...navItems, ITEM_MEU_PERFIL].map(item => {
            if (item.children) {
              const hasActiveChild = isChildActive(item.children);
              const isOpen = openSubMenu[item.name] !== undefined ? openSubMenu[item.name] : hasActiveChild;
              
              return (
                <div key={item.name} className="nav-item-with-submenu">
                  <div
                    className={`nav-item parent-item ${hasActiveChild ? 'active' : ''}`}
                    title={collapsed ? item.name : undefined}
                    onClick={() => {
                      if (collapsed) {
                        // Menu recolhido: expande o menu e já abre este grupo.
                        setCollapsed(false);
                        setOpenSubMenu(prev => ({ ...prev, [item.name]: true }));
                      } else {
                        setOpenSubMenu(prev => ({ ...prev, [item.name]: !isOpen }));
                      }
                    }}
                  >
                    <div className="nav-item-left">
                      <item.icon size={18} />
                      {!collapsed && <span>{item.name}</span>}
                    </div>
                    {!collapsed && (
                      <ChevronDown 
                        size={16} 
                        className={`submenu-chevron ${isOpen ? 'open' : ''}`} 
                      />
                    )}
                  </div>
                  
                  {!collapsed && isOpen && (
                    <div className="submenu">
                      {filhosVisiveis(item).map(child => {
                        // Títulos Asaas/CPFL funcionam como controles expansíveis.
                        if (child.titulo && child.grupo) {
                          const groupKey = `${item.name}:${child.grupo}`;
                          const isGroupOpen = Boolean(openBillingGroups[groupKey]);

                          return (
                            <div
                              key={`titulo-${child.titulo}`}
                              className="submenu-group-title"
                              role="button"
                              tabIndex={0}
                              aria-expanded={isGroupOpen}
                              onClick={() => toggleBillingGroup(groupKey)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleBillingGroup(groupKey);
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                            >
                              <span>{child.titulo}</span>
                              <ChevronDown
                                size={14}
                                className={`submenu-chevron ${isGroupOpen ? 'open' : ''}`}
                              />
                            </div>
                          );
                        }

                        // Mantém títulos antigos sem grupo como rótulos estáticos.
                        if (child.titulo) {
                          return (
                            <div key={`titulo-${child.titulo}`} className="submenu-group-title">
                              {child.titulo}
                            </div>
                          );
                        }

                        // Os links só aparecem quando o grupo correspondente está aberto.
                        if (
                          child.grupo &&
                          !openBillingGroups[`${item.name}:${child.grupo}`]
                        ) {
                          return null;
                        }

                        const agrupado = item.children.some(c => c.titulo);
                        const isActive = location.pathname === child.path;
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`submenu-item ${agrupado ? 'submenu-item-grouped' : ''} ${isActive ? 'active' : ''}`}
                            onClick={aoNavegarMenu}
                          >
                            <child.icon size={16} />
                            <span>{child.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={aoNavegarMenu}
                className={`nav-item ${isActive ? 'active' : ''}`}
                title={collapsed ? item.name : undefined}
              >
                <item.icon size={18} />
                {!collapsed && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>
      
      <main className="main-content">
        <header className="mobile-header">
          <button onClick={() => setSidebarOpen(true)} className="menu-button">
            <Menu size={24} />
          </button>
          <div className="mobile-logo">
            <img src={logoSoulidari} alt="Logo Soulidari" className="mobile-logo-image" />
          </div>
        </header>
        <div className="content-area"><Outlet /></div>
      </main>
    </div>
  );
}
